import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '../../internal/dds-hu-element'
import ArrowsArrowRightSLine from '../icons/arrows/arrows-arrow-right-s-line/arrows-arrow-right-s-line.component'
import styles from './sidenav-group.scss?inline'

export type Spacing = 'top' | 'bottom' | 'both' | 'none'

/**
 * `dap-ds-sidenav-group`
 * @summary Side navigation group
 * @element dap-ds-sidenav-group
 * @title - Side navigation group
 * @group side-navigation
 *
 * @slot - The content of the side navigation group.
 * @slot submenu - The submenu of the side navigation group.
 * @slot suffix - The suffix of the side navigation group.
 *
 * @csspart base - The main side navigation group container.
 * @csspart title - The title of the side navigation group.
 * @csspart link - The link of the side navigation group.
 * @csspart toggle - The toggle button of the side navigation group.
 * @csspart submenu - The submenu of the side navigation group.
 *
 * @cssproperty --dds-sidenav-group-margin-bottom - Default bottom margin of the sidenav group (default: var(--dds-spacing-100))
 * @cssproperty --dds-sidenav-group-border-radius - Border radius of the sidenav group (default: var(--dds-radius-base))
 * @cssproperty --dds-sidenav-group-spacing-margin - Margin used for spacing variants (default: var(--dds-spacing-400))
 * @cssproperty --dds-sidenav-group-item-min-height-xs - Minimum height of the sidenav group item (default: var(--dds-spacing-800))
 * @cssproperty --dds-sidenav-group-item-min-height-sm - Minimum height of the sidenav group item (default: var(--dds-spacing-1000))
 * @cssproperty --dds-sidenav-group-item-min-height-lg - Minimum height of the sidenav group item (default: var(--dds-spacing-1200))
 * @cssproperty --dds-sidenav-group-item-padding-xs - Padding of the sidenav group item (default: var(--dds-spacing-100) var(--dds-spacing-300))
 * @cssproperty --dds-sidenav-group-item-padding-sm - Padding of the sidenav group item (default: var(--dds-spacing-200) var(--dds-spacing-400))
 * @cssproperty --dds-sidenav-group-item-padding-lg - Padding of the sidenav group item (default: var(--dds-spacing-300) var(--dds-spacing-500))
 * @cssproperty --dds-sidenav-group-item-color - Text color of the sidenav group item (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-sidenav-group-item-font-size-xs - Font size of the sidenav group item (default: var(--dds-font-sm))
 * @cssproperty --dds-sidenav-group-item-font-size-sm - Font size of the sidenav group item (default: var(--dds-font-base))
 * @cssproperty --dds-sidenav-group-item-font-size-lg - Font size of the sidenav group item (default: var(--dds-font-lg))
 * @cssproperty --dds-sidenav-group-item-font-weight - Font weight of the sidenav group item (default: var(--dds-font-weight-medium))
 * @cssproperty --dds-sidenav-group-item-bg-active - Background color when the item is active (default: var(--dds-transparent-black-subtle))
 * @cssproperty --dds-sidenav-group-item-bg-hover - Background color on hover and active states (default: var(--dds-transparent-black-base))
 * @cssproperty --dds-sidenav-group-toggle-margin - Margin for the toggle button (default: var(--dds-spacing-300))
 * @cssproperty --dds-sidenav-group-submenu-padding - Padding for the submenu (default: var(--dds-spacing-300) 0)
 */
export default class DapDSSideNavGroup extends DdsElement {
  static tagName = 'dap-ds-sidenav-group'

  static dependencies = {
    'dap-ds-icon-arrow-right-s-line': ArrowsArrowRightSLine,
  }

  /** The href of the side navigation item */
  @property({ reflect: true }) href: string

  /** Whether the side navigation item is active */
  @property({ type: Boolean, reflect: true }) active = false

  /** The spacing of the side navigation item
   * @type {'top' | 'bottom' | 'both' | 'none'}
   */
  @property({ reflect: true }) spacing: Spacing = 'none'

  /** Whether the side navigation group is open */
  @property({ type: Boolean, reflect: true }) open = false

  /** The size of the side navigation group */
  @property({ reflect: true, type: String }) size: 'xs' | 'sm' | 'lg' = 'xs'

  static readonly styles = unsafeCSS(styles)

  render() {
    return html`
      <div
        part="base"
        role="listitem"
        class=${clsx(
          'sidenav-group',
          `sidenav-group--spacing-${this.spacing}`,
        )}>
        <div
          class=${clsx(
            'sidenav-group__item',
            `sidenav-group__item--${this.size}`,
            {
              'sidenav-group__item--active': this.active,
            },
          )}>
          <div
            class=${clsx('sidenav-group__title')}
            part="title"
            id="title"
            @click=${(e: any) => {
              e.preventDefault()
              e.stopPropagation()
              if (!this.open) {
                this.open = true
              } else if (this.open && !this.href) {
                this.open = false
              }
            }}>
            ${this.href
              ? html`<a
                  href=${this.href}
                  class="sidenav-group__link"
                  part="link">
                  <slot></slot>
                  <slot name="suffix"></slot>
                </a>`
              : html`<slot></slot>`}
          </div>
          <button
            part="toggle"
            type="button"
            class=${clsx('sidenav-group__toggle', {
              'sidenav-group__toggle--open': this.open,
            })}
            aria-labelledby="title"
            aria-expanded=${this.open ? 'true' : 'false'}
            aria-controls="submenu"
            @click=${(e: any) => {
              e.preventDefault()
              e.stopPropagation()
              this.open = !this.open
            }}>
            <dap-ds-icon-arrow-right-s-line
              part="icon"
              aria-hidden="true"></dap-ds-icon-arrow-right-s-line>
          </button>
        </div>
        <ul
          part="submenu"
          id="submenu"
          role="list"
          class=${clsx('sidenav-group__submenu', {
            'sidenav-group__submenu--open': this.open,
          })}>
          <slot name="submenu"></slot>
        </ul>
      </div>
    `
  }
}
